import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export function getScoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 7) return 'success';
  if (score >= 5) return 'warning';
  return 'error';
}

export function getBandLevel(score: number): string {
  if (score >= 8.5) return 'Expert';
  if (score >= 7.5) return 'Very Good';
  if (score >= 6.5) return 'Good';
  if (score >= 5.5) return 'Moderate';
  if (score >= 4.5) return 'Limited';
  return 'Beginner';
}

export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function validateWordCount(
  text: string,
  min: number,
  max?: number
): { isValid: boolean; count: number; message?: string } {
  const count = countWords(text);
  
  if (count < min) {
    return {
      isValid: false,
      count,
      message: `Minimum ${min} words required`,
    };
  }
  
  if (max && count > max) {
    return {
      isValid: false,
      count,
      message: `Maximum ${max} words exceeded`,
    };
  }
  
  return {
    isValid: true,
    count,
  };
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
